import type * as React from 'react';
import { IvvGateChip } from '@/components/ui/ivv-gate-chip';
import { cn } from '@/lib/utils/cn';
import { dateValue, formatDate, formatDayMonth, formatMonth } from '@/lib/utils/format';
import { ChartTooltip, useChartTooltip } from './chart-tooltip';
import { ChartEmpty, ChartLegend, SrTable, cssVars, plural } from './shared';
import { useEntryMotion } from './use-entry-motion';

/**
 * MilestoneGantt — milestones on a month axis (prototype `gantt()`): the
 * planned date as a hollow diamond, the projected (or done) date as a
 * filled one coloured by status, the slip between them shaded, today as
 * an amber line labelled at the bottom, month gridlines behind. The
 * compact variant (Overview's road to go-live) has narrower name and meta
 * columns and no gate tags.
 *
 * The slip in days and the status come from the API; the chart only
 * places each date on the axis.
 */
export type MilestoneStatus = 'done' | 'late' | 'at-risk' | 'on-track' | 'compressed';

export const MILESTONE_STATUS_LABEL: Record<MilestoneStatus, string> = {
  done: 'Done',
  late: 'Late',
  'at-risk': 'At risk',
  'on-track': 'On track',
  compressed: 'Compressed',
};

export interface GanttMilestone {
  id: string;
  name: string;
  /** ISO dates. */
  planned: string;
  /** Forecast, or the actual date when done. */
  projected: string;
  status: MilestoneStatus;
  /** Days the projection sits after the plan, from the API (0 when on plan). */
  slipDays: number;
  /** Named as an IV&V gate. */
  gate?: boolean;
  note?: string | null;
}

export interface MilestoneGanttProps {
  milestones: GanttMilestone[];
  /** Today (ISO); the amber line. */
  today: string;
  compact?: boolean;
  emptyText?: string;
  renderName?: (m: GanttMilestone) => React.ReactNode;
  motionKey?: string | number;
  className?: string;
}

const MARK_BG: Record<MilestoneStatus, string> = {
  done: 'bg-ink-500',
  late: 'bg-posture-critical',
  'at-risk': 'bg-posture-at-risk',
  compressed: 'bg-posture-at-risk',
  'on-track': 'bg-posture-healthy',
};

const SLIP: Record<MilestoneStatus, string> = {
  done: 'bg-ink-100 border-ink-300',
  late: 'bg-posture-critical-tint border-posture-critical',
  'at-risk': 'bg-posture-at-risk-tint border-posture-at-risk',
  compressed: 'bg-posture-at-risk-tint border-posture-at-risk',
  'on-track': 'bg-posture-at-risk-tint border-posture-at-risk',
};

const DIAMOND = 'absolute top-1/2 z-[2] -ml-1.5 -mt-1.5 h-3 w-3 rotate-45 rounded-[2px]';

export function MilestoneGantt({
  milestones,
  today,
  compact = false,
  emptyText,
  renderName,
  motionKey,
  className,
}: MilestoneGanttProps) {
  const { animate, runKey } = useEntryMotion(motionKey);
  const { tip, bind } = useChartTooltip();

  if (!milestones.length) {
    return (
      <ChartEmpty title="No milestones yet" className={className}>
        {emptyText ??
          (compact
            ? 'Add the milestones from the project plan on the Schedule domain; the road to go-live draws itself from them.'
            : 'Add the milestones from the project plan below; the road to go-live draws itself from them.')}
      </ChartEmpty>
    );
  }

  const now = dateValue(today);
  const all = milestones.flatMap((m) => [dateValue(m.planned), dateValue(m.projected)]).concat(now);
  const lo = new Date(Math.min(...all));
  const hi = new Date(Math.max(...all));
  const a = new Date(lo.getFullYear(), lo.getMonth(), 1).getTime();
  const b = new Date(hi.getFullYear(), hi.getMonth() + 1, 1).getTime();
  const pos = (ms: number) => ((ms - a) / (b - a)) * 100;
  const months: number[] = [];
  for (let d = new Date(a); d.getTime() < b; d = new Date(d.getFullYear(), d.getMonth() + 1, 1)) {
    months.push(d.getTime());
  }

  const cols = compact ? 'grid-cols-[168px_1fr_116px]' : 'grid-cols-[280px_1fr_120px]';
  const inset = compact ? 'left-[168px] right-[116px]' : 'left-[280px] right-[120px]';

  return (
    <div key={runKey} className={cn('relative pb-5', animate && 'chart-anim', className)}>
      <div aria-hidden className={cn('pointer-events-none absolute bottom-[18px] top-0', inset)}>
        {months.map((ms) => (
          <span
            key={ms}
            className="absolute inset-y-0 w-px bg-ink-100"
            style={{ left: `${pos(ms)}%` }}
          />
        ))}
        <span
          className="absolute inset-y-0 w-0 border-l-2 border-accent-amber-600"
          style={{ left: `${pos(now)}%` }}
        >
          <span className="absolute -bottom-0.5 left-1 whitespace-nowrap bg-paper-0 px-[3px] text-[10px] font-semibold text-accent-amber-700">
            Today
          </span>
        </span>
      </div>
      <div aria-hidden className={cn('grid h-[22px] items-center text-[11px] text-ink-500', cols)}>
        <span />
        <div className="relative h-full">
          {months.map((ms) => (
            <span
              key={ms}
              className="absolute top-0.5 translate-x-1"
              style={{ left: `${pos(ms)}%` }}
            >
              {formatMonth(ms)}
            </span>
          ))}
        </div>
        <span />
      </div>
      <div>
        {milestones.map((m, i) => {
          const p = pos(dateValue(m.planned));
          const q = pos(dateValue(m.projected));
          const slip = m.slipDays > 0;
          const label = MILESTONE_STATUS_LABEL[m.status];
          const when = formatDayMonth(m.projected);
          return (
            <div
              key={m.id}
              className={cn('a-rise relative grid h-10 items-center border-t border-ink-100', cols)}
              style={cssVars({ '--i': i })}
            >
              <div className="flex items-center gap-1.5 overflow-hidden whitespace-nowrap pr-2 text-[13px] text-ink-900">
                <span className="truncate">{renderName ? renderName(m) : m.name}</span>
                {m.gate && !compact ? (
                  <IvvGateChip className="h-4 border border-ink-200 bg-transparent px-1.5 text-[10px] font-normal leading-4 text-ink-500" />
                ) : null}
              </div>
              <div className="relative h-full">
                {slip ? (
                  <span
                    className={cn(
                      'a-slip absolute top-1/2 z-[1] -mt-0.5 h-1 border-y',
                      SLIP[m.status],
                    )}
                    style={{ ...cssVars({ '--i': i }), left: `${p}%`, width: `${q - p}%` }}
                  />
                ) : null}
                {slip ? (
                  <span
                    className={cn(DIAMOND, 'border-2 border-ink-300 bg-paper-0')}
                    style={{ left: `${p}%` }}
                    {...bind({ title: m.name, value: `Planned ${formatDate(m.planned)}` })}
                  />
                ) : null}
                <span
                  className={cn(
                    DIAMOND,
                    'a-slide shadow-[0_0_0_2px_var(--paper-0)]',
                    MARK_BG[m.status],
                  )}
                  style={{ ...cssVars({ '--i': i, '--pl': `${p}%` }), left: `${q}%` }}
                  {...bind({
                    title: m.name,
                    value: `${m.status === 'done' ? 'Done' : 'Projected'} ${formatDate(m.projected)}`,
                    sub: [label, slip ? `${m.slipDays} days after plan` : null, m.note ?? null]
                      .filter(Boolean)
                      .join(' · '),
                  })}
                />
              </div>
              <div className="whitespace-nowrap text-right text-[12px] text-ink-500">
                {slip ? (
                  <>
                    <b className="font-semibold text-ink-900">+{m.slipDays} days</b> · {when}
                  </>
                ) : (
                  `${label} · ${when}`
                )}
              </div>
            </div>
          );
        })}
      </div>
      <SrTable
        caption="Milestones, planned and projected"
        head={['Milestone', 'Planned', 'Projected', 'Status', 'After plan', 'IV&V gate']}
        rows={milestones.map((m) => [
          m.name,
          formatDate(m.planned),
          formatDate(m.projected),
          [MILESTONE_STATUS_LABEL[m.status], m.note].filter(Boolean).join(' · '),
          m.slipDays > 0 ? plural(m.slipDays, 'day') : 'On plan',
          m.gate ? 'Yes' : 'No',
        ])}
      />
      <ChartTooltip tip={tip} />
    </div>
  );
}

/** The road card's key: planned hollow, projected filled. */
export function MilestoneLegend({ className }: { className?: string }) {
  return (
    <ChartLegend
      className={className}
      items={[
        { label: 'Planned', swatch: 'diamond-hollow', color: 'border-ink-300' },
        { label: 'Projected', swatch: 'diamond', color: 'bg-ink-700' },
      ]}
    />
  );
}
